import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { useFieldControl } from '@/components/ui/field-context';
import { cn } from '@/lib/utils';

export interface NativeSelectProps extends React.ComponentProps<'select'> {
  /** `sm` : 28px, pour une barre d'outils de tableau (`select-sm`). */
  controlSize?: 'default' | 'sm';
  /** Classes du conteneur (largeur, marge). `className` s'applique au <select>. */
  wrapperClassName?: string;
}

/**
 * `<select>` natif stylé (`select-wrap` + `select` des maquettes ; « Native
 * Select » de shadcn/ui). À préférer au Select Radix dans les formulaires
 * react-hook-form (`{...register('x')}`) : il garde le comportement natif.
 */
const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ className, wrapperClassName, controlSize = 'default', ...props }, ref) => {
    const a11y = useFieldControl(props);
    return (
      <div className={cn('relative min-w-0', wrapperClassName)}>
        <select
          ref={ref}
          className={cn(
            'w-full min-w-0 appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
            'aria-[invalid=true]:border-danger-fg',
            controlSize === 'sm' ? 'h-7 text-[13px]' : 'h-8',
            className,
          )}
          {...props}
          {...a11y}
        />
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
      </div>
    );
  },
);
NativeSelect.displayName = 'NativeSelect';

export { NativeSelect };
